import Swiper from 'swiper'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'
import "../styles/swiper.css"

// Generic slider types

// Swiper recomputes params.breakpoints from originalParams.breakpoints on
// every resize, so a one-time mutation in `init` gets clobbered as soon as
// the viewport crosses a breakpoint. Patch originalParams too and re-apply
// on resize/breakpoint change, not just on init.
function forceSingleSlideView(swiper, { groupToo = false } = {}) {
    if (swiper.slides.length !== 1) return
    swiper.params.slidesPerView = 1
    swiper.originalParams.slidesPerView = 1
    if (groupToo) {
        swiper.params.slidesPerGroup = 1
        swiper.originalParams.slidesPerGroup = 1
    }
    [swiper.params.breakpoints, swiper.originalParams.breakpoints].forEach(breakpoints => {
        if (!breakpoints) return
        Object.values(breakpoints).forEach(bp => {
            bp.slidesPerView = 1
            if (groupToo) bp.slidesPerGroup = 1
        })
    })
    swiper.update()
}

function init() {
    const simpleSwiper = new Swiper(".simple-swiper", {
        modules: [Pagination, Navigation],
        slidesPerView: 1.15,
        spaceBetween: 8,
        pagination: {
            el: ".swiper-pagination",
            clickable: true,
        },
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
        },
        breakpoints: {
            768: {
                slidesPerView: 1,
            },
        },
        on: {
            init: forceSingleSlideView,
            resize: forceSingleSlideView,
        }
    });
    const tinySwiper = new Swiper(".tiny-swiper", {
        modules: [Pagination],
        slidesPerView: 1.15,
        spaceBetween: 24,
        pagination: {
            el: ".swiper-pagination",
            clickable: true,
        },
        breakpoints: {
            768: {
                slidesPerView: 1,
            },
        },
        on: {
            init: forceSingleSlideView,
            resize: forceSingleSlideView,
        }
    })
    const imageSwiper = new Swiper(".image-swiper", {
        modules: [Pagination, Navigation],
        slidesPerView: 1.15,
        spaceBetween: 8,
        loop: true,
        pagination: {
            el: ".swiper-pagination",
            clickable: true,
        },
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
        },
        breakpoints: {
            768: {
                slidesPerView: 1,
            },
        },
        on: {
            init(swiper) {
                if (swiper.slides.length === 1) {
                    swiper.params.loop = false
                    swiper.originalParams.loop = false
                }
                forceSingleSlideView(swiper)
            },
            resize: forceSingleSlideView,
        }
    })

    const items = document.querySelectorAll('.item-swiper')
    items.forEach(item => {
        const itemSwiper = new Swiper(item, {
            modules: [Pagination, Navigation],
            spaceBetween: 8,
            rewind: true,
            slidesPerView: 1.15,
            breakpoints: {
                640: {
                    slidesPerView: 2,
                    slidesPerGroup: 2,
                },
                1024: {
                    slidesPerView: item.dataset.slideSize ?? 2,
                    slidesPerGroup: item.dataset.slideSize ?? 2,
                    spaceBetween: 16,
                },
            },
            pagination: {
                el: ".swiper-pagination",
                clickable: true,
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            on: {
                init: swiper => forceSingleSlideView(swiper, { groupToo: true }),
                resize: swiper => forceSingleSlideView(swiper, { groupToo: true }),
            }
        })
    })

    const nextEventSliders = document.querySelectorAll('[data-next-event-slider]')
    nextEventSliders.forEach(item => {
        const autoscroll = 'autoscroll' in item.dataset
        const nextEventSwiper = new Swiper(item, {
            modules: [Pagination, Navigation, ...(autoscroll ? [Autoplay] : [])],
            slidesPerView: 1.15,
            rewind: true,
            breakpoints: {
                768: {
                    slidesPerView: 1,
                },
            },
            pagination: {
                el: ".swiper-pagination",
                clickable: true,
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            ...(autoscroll ? {
                autoplay: {
                    delay: 5000,
                    disableOnInteraction: false,
                },
            } : {}),
            on: {
                init: forceSingleSlideView,
                resize: forceSingleSlideView,
            }
        })

        const rows = item.querySelectorAll('[data-next-event-row]')
        if (rows.length) {
            const syncRowHeights = () => {
                rows.forEach(row => { row.style.height = '' })
                const tallest = Math.max(...Array.from(rows).map(row => row.scrollHeight))
                rows.forEach(row => { row.style.height = tallest + 'px' })
                nextEventSwiper.update()
            }

            // Measuring scrollHeight immediately can undershoot: Swiper may not
            // have finished applying the (narrower, peeked) slide width yet, and
            // web fonts may not have swapped in, so text wraps to more lines
            // later than this snapshot accounts for. A too-short fixed height
            // then lets real content overflow into the nav row below it.
            requestAnimationFrame(syncRowHeights)
            document.fonts?.ready.then(syncRowHeights)
            window.addEventListener('resize', syncRowHeights)
        }
    })

    const newsSliders = document.querySelectorAll('[data-news-slider]')
    newsSliders.forEach(item => {
        const itemSwiper = new Swiper(item, {
            modules: [Pagination, Navigation],
            spaceBetween: 8,
            rewind: true,
            slidesPerView: 1.15,
            breakpoints: {
                720: {
                spaceBetween: 24,
                slidesPerView: item.dataset.newsSize ?? 3,
                    slidesPerGroup: item.dataset.newsSize ?? 3,
                    spaceBetween: 16,
                },
            },
            pagination: {
                el: ".swiper-pagination",
                clickable: true,
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            on: {
                init: swiper => forceSingleSlideView(swiper, { groupToo: true }),
                resize: swiper => forceSingleSlideView(swiper, { groupToo: true }),
            }
        })
    })
}

export default {
    init
};
